<script setup>
/**
 * GlPagination — page navigation control.
 *
 * Forward-compatible with @gitlab/ui's GlPagination:
 *   - Supports `modelValue` (v-model), `perPage`, `totalItems` for standard mode
 *   - Also accepts a `series` prop for Pagy-style pre-computed page lists
 *   - Emits `update:modelValue` (v-model), `previous`, `next` like the real GlPagination
 */
import { computed } from "vue";
import { iconHref } from "~/utils/icons.js";

const { labelNav, labelNextPage, labelPrevPage, modelValue, pages, perPage, are, series, totalItems } = defineProps({
  /** aria-label for the nav element. */
  labelNav: { default: "Pagination", type: String },
  /** aria-label for next page button. */
  labelNextPage: { default: "Go to next page", type: String },
  /** aria-label for previous page button. */
  labelPrevPage: { default: "Go to previous page", type: String },
  /** Current page number (v-model). */
  modelValue: { default: 1, type: Number },
  /** Total page count — used alongside series when totalItems is not provided. */
  pages: { default: null, type: Number },
  /** Items per page — used with totalItems to compute pages. */
  perPage: { default: 20, type: Number },
  /**
   * Pre-computed series array from Pagy.
   * Items are: Number (other page), String of digits (current page), or "gap".
   * When provided, totalItems/perPage are ignored for page computation.
   */
  series: { default: null, type: Array },
  /** Total number of items — used with perPage to compute pages. */
  totalItems: { default: 0, type: Number }
});

const emit = defineEmits(["update:modelValue", "previous", "next"]);

const totalPages = computed(() => {
  if (pages != null) return pages;
  if (totalItems > 0 && perPage > 0) {
    return Math.ceil(totalItems / perPage);
  }
  return 1;
});

/**
 * Normalised series — each item is { type, page, label }
 *   type: 'page' | 'current' | 'gap'
 */
const normalisedSeries = computed(() => {
  if (series) {
    return series.map((item) => {
      if (item === "gap") return { type: "gap" };
      if (typeof item === "string") return { label: item, page: Number(item), type: "current" };
      return { label: String(item), page: item, type: "page" };
    });
  }

  const pages = totalPages.value;
  const current = modelValue;
  const items = [];
  for (let p = 1; p <= pages; p++) {
    if (p === current) {
      items.push({ label: String(p), page: p, type: "current" });
    } else {
      items.push({ label: String(p), page: p, type: "page" });
    }
  }
  return items;
});

const prevPage = computed(() => (modelValue > 1 ? modelValue - 1 : null));
const nextPage = computed(() => (modelValue < totalPages.value ? modelValue + 1 : null));

function goTo(page) {
  if (page !== modelValue) {
    emit("update:modelValue", page);
  }
}

function goPrev() {
  if (prevPage.value) {
    emit("previous");
    goTo(prevPage.value);
  }
}

function goNext() {
  if (nextPage.value) {
    emit("next");
    goTo(nextPage.value);
  }
}
</script>

<template>
  <nav v-if="totalPages > 1" :aria-label="labelNav" class="gl-pagination mt-6 flex justify-center">
    <ul class="flex items-center gap-0.5 list-none p-0 m-0">
      <!-- Previous -->
      <li :class="{ disabled: !prevPage }">
        <span v-if="!prevPage" class="gl-pagination-item" aria-disabled="true">
          <svg class="h-4 w-4" aria-hidden="true">
            <use :href="iconHref('chevron-lg-left')" />
          </svg>
        </span>
        <button v-else type="button" class="gl-pagination-item" :aria-label="labelPrevPage" @click="goPrev">
          <svg class="h-4 w-4" aria-hidden="true">
            <use :href="iconHref('chevron-lg-left')" />
          </svg>
        </button>
      </li>

      <!-- Page series -->
      <li v-for="(item, idx) in normalisedSeries" :key="idx" :class="{ disabled: item.type === 'gap' }">
        <!-- Ellipsis gap -->
        <span v-if="item.type === 'gap'" class="gl-pagination-item" aria-disabled="true">…</span>

        <!-- Current page -->
        <button
          v-else-if="item.type === 'current'"
          type="button"
          class="gl-pagination-item active"
          aria-current="page"
          :aria-label="`Page ${item.label}, current page`"
          disabled
        >
          {{ item.label }}
        </button>

        <!-- Other page -->
        <button
          v-else
          type="button"
          class="gl-pagination-item"
          :aria-label="`Go to page ${item.label}`"
          @click="goTo(item.page)"
        >
          {{ item.label }}
        </button>
      </li>

      <!-- Next -->
      <li :class="{ disabled: !nextPage }">
        <span v-if="!nextPage" class="gl-pagination-item" aria-disabled="true">
          <svg class="h-4 w-4" aria-hidden="true">
            <use :href="iconHref('chevron-lg-right')" />
          </svg>
        </span>
        <button v-else type="button" class="gl-pagination-item" :aria-label="labelNextPage" @click="goNext">
          <svg class="h-4 w-4" aria-hidden="true">
            <use :href="iconHref('chevron-lg-right')" />
          </svg>
        </button>
      </li>
    </ul>
  </nav>
</template>
